import { useState } from "react";

import { Button } from "@/components/ui/Button";
import { Dialog } from "@/components/ui/Dialog";

interface EditorialDeleteConfirmDialogProps {
  open: boolean;
  title: string;
  message: string;
  cancelLabel: string;
  confirmLabel: string;
  deletingLabel: string;
  error?: string | null;
  isDeleting: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

export function EditorialDeleteConfirmDialog({
  open,
  title,
  message,
  cancelLabel,
  confirmLabel,
  deletingLabel,
  error = null,
  isDeleting,
  onClose,
  onConfirm,
}: EditorialDeleteConfirmDialogProps) {
  return (
    <Dialog open={open} onClose={onClose} title={title}>
      <div className="space-y-4">
        <p className="text-sm text-neutral-600">{message}</p>
        {error ? (
          <p className="text-sm text-red-600" role="alert">
            {error}
          </p>
        ) : null}
        <div className="flex justify-end gap-2">
          <Button
            type="button"
            variant="secondary"
            onClick={onClose}
            disabled={isDeleting}
          >
            {cancelLabel}
          </Button>
          <Button
            type="button"
            variant="danger"
            onClick={onConfirm}
            disabled={isDeleting}
            aria-busy={isDeleting}
            className="inline-flex items-center gap-2"
          >
            {isDeleting ? (
              <span
                aria-hidden
                className="inline-block size-3.5 animate-spin rounded-full border-2 border-white border-r-transparent"
              />
            ) : null}
            {isDeleting ? deletingLabel : confirmLabel}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

export function useEditorialDeleteConfirm(onDelete: () => Promise<void>) {
  const [open, setOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function requestDelete() {
    setError(null);
    setOpen(true);
  }

  function closeDialog() {
    if (!isDeleting) {
      setOpen(false);
      setError(null);
    }
  }

  async function confirmDelete() {
    setIsDeleting(true);
    setError(null);

    try {
      await onDelete();
      setOpen(false);
    } catch (caughtError) {
      console.error("[useEditorialDeleteConfirm]", caughtError);
      setError(
        caughtError instanceof Error ? caughtError.message : "delete_failed",
      );
    } finally {
      setIsDeleting(false);
    }
  }

  return {
    open,
    isDeleting,
    error,
    requestDelete,
    closeDialog,
    confirmDelete,
  };
}
